import {
    Component,
    ViewChild,
    OnInit,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { OverlayPanel } from "primeng/overlaypanel";
import { SidebarModule } from "primeng/sidebar";
import { Sidebar } from "primeng/sidebar";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ResponsiveService } from "@/components/shared/responsive/responsive.service";
import { UserService } from "@/_services";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import {
    OnboardingStepResponse,
    OnboardingStepUI,
} from "@/_models/onboarding-step-response.interface";
import { UserDm } from "@/_models/user-dm";
import { DismissedBanner } from "@/_models";
import { ToastService } from "@/_services/ToastService";
import UserRoles = UserDm.UserRoles;

type StepKey =
    | "setupRecipients"
    | "inviteUsers"
    | "shareAccess"
    | "addAircraft"
    | "integrateScheduling"
    | "uploadManuals";

interface StepConfig {
    key: StepKey;
    action: (component: OnboardingGuideComponent) => void;
    enabled: (component: OnboardingGuideComponent) => boolean;
    bannerType?: UserDm.BannerDismissType;
}

@UntilDestroy()
@Component({
    selector: "app-onboarding-guide",
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        OverlayPanelModule,
        SidebarModule,
        SharedComponentsModule,
    ],
    styleUrls: ["./onboarding-guide.component.scss"],
    templateUrl: "./onboarding-guide.component.html",
})
export class OnboardingGuideComponent implements OnInit, OnChanges {
    @ViewChild("op") op!: OverlayPanel;
    @ViewChild("sidebar") sidebar!: Sidebar;
    @Input() asapFormId: string | null = null;
    @Input() steps: OnboardingStepResponse[] = [];
    @Output() stepsChange = new EventEmitter<OnboardingStepResponse[]>();

    displaySteps: Array<
        OnboardingStepResponse & OnboardingStepUI & { isOpen: boolean }
    > = [];
    isMobile: boolean = false;
    sidebarVisible = false;
    isAdmin: boolean = false;

    private stepConfigs: Record<StepKey, StepConfig> = {
        setupRecipients: {
            key: "setupRecipients",
            action: (comp) => comp.navigateToRecipients(),
            enabled: (comp) => !!comp.asapFormId,
        },
        inviteUsers: {
            key: "inviteUsers",
            action: (comp) => comp.navigateToTeam(),
            enabled: () => true,
            bannerType: DismissedBanner.AccountSetupTeamNotApplicable,
        },
        shareAccess: {
            key: "shareAccess",
            action: (comp) => comp.printQrCode(),
            enabled: (comp) => !!comp.asapFormId,
        },
        addAircraft: {
            key: "addAircraft",
            action: (comp) => comp.navigateToAircraft(),
            enabled: () => true,
            bannerType: DismissedBanner.AccountSetupAircraftNotApplicable,
        },
        integrateScheduling: {
            key: "integrateScheduling",
            action: (comp) => comp.navigateToIntegrations(),
            enabled: () => true,
            bannerType: DismissedBanner.AccountSetupSchedulingNotApplicable,
        },
        uploadManuals: {
            key: "uploadManuals",
            action: (comp) => comp.navigateToManuals(),
            enabled: () => true,
            bannerType: DismissedBanner.AccountSetupManualsNotApplicable,
        },
    };

    constructor(
        private router: Router,
        private responsiveService: ResponsiveService,
        private userService: UserService,
        private onboardingGuideService: OnboardingGuideService,
        private toastService: ToastService,
    ) {}

    ngOnInit(): void {
        this.checkInitialScreenSize();
        this.responsiveService.responsiveChangeSubject.subscribe();

        this.userService
            .getCurrentUserAsync()
            .pipe(untilDestroyed(this))
            .subscribe((user) => {
                this.isAdmin = user.hasAtLeastRole(UserRoles.Admin);
                this.updateDisplaySteps();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["steps"]) {
            this.updateDisplaySteps();
        }
    }

    private getStepUI(stepKey: StepKey): OnboardingStepUI {
        const isAsap = this.isAsapAccount;
        const inviteUsersCanMarkNotApplicable = !isAsap;

        const uiMap: Record<StepKey, OnboardingStepUI> = {
            setupRecipients: {
                title: "Set Up Report Recipients",
                description:
                    "As the account owner, you'll always receive ASAP reports as your operation's Safety Officer. Add any additional recipients that should receive reports, such as ACSF, your FAA/FSDO contact, or another company safety manager.",
                cta: "Add Recipients",
                canMarkNotApplicable: false,
            },
            inviteUsers: {
                title: "Invite Your Team",
                description: isAsap
                    ? "Safety is a team effort. Invite your colleagues to join your RISE SMS account so everyone can submit ASAP reports when needed."
                    : "Safety is a team effort. Invite your colleagues to join your RISE SMS account so everyone can participate in the safety of your operations.",
                cta: "Invite Team Members",
                canMarkNotApplicable: inviteUsersCanMarkNotApplicable,
            },
            shareAccess: {
                title: "Share Instant Access",
                description:
                    "Make reporting effortless. Download your ASAP form's QR code and post it where your team can see it. With one quick scan, anyone in your organization can submit a report immediately.",
                cta: "Print QR Code",
                canMarkNotApplicable: false,
            },
            addAircraft: {
                title: "Add Aircraft",
                description:
                    "Add your aircraft to RISE SMS so you can link reports, assessments, and safety data directly to each tail. If you're not a flight department, simply mark this step as not applicable.",
                cta: "Add Aircraft",
                canMarkNotApplicable: true,
            },
            integrateScheduling: {
                title: "Integrate Scheduling",
                description:
                    "Integrate your scheduling software to generate risk assessments and automatically share safety data with the other systems you use.",
                cta: "Set Up Integration",
                canMarkNotApplicable: true,
            },
            uploadManuals: {
                title: "Upload Manuals",
                description:
                    "Upload your manuals to keep critical documents organized, accessible, and connected to your safety management system.",
                cta: "Upload Manuals",
                canMarkNotApplicable: true,
            },
        };
        return uiMap[stepKey];
    }

    get isAsapAccount(): boolean {
        return this.steps.some(
            (step) =>
                step.step === "setupRecipients" || step.step === "shareAccess",
        );
    }

    get guideTitle(): string {
        return this.isAsapAccount ? "Quick Start Guide" : "Account Setup";
    }

    private updateDisplaySteps(): void {
        if (!this.steps || this.steps.length === 0) {
            this.displaySteps = [];
            return;
        }
        this.displaySteps = this.steps.map((step, index) => {
            const uiData = this.getStepUI(step.step as StepKey);
            return {
                ...step,
                title: uiData.title,
                description: uiData.description,
                cta: uiData.cta,
                canMarkNotApplicable: uiData.canMarkNotApplicable,
                isOpen: index === 0,
            };
        });
    }

    private checkInitialScreenSize() {
        if (this.responsiveService.isMobile !== undefined) {
            this.isMobile = this.responsiveService.isMobile;
        }
    }

    private ensureOneSectionOpen() {
        if (this.displaySteps.every((step) => !step.isOpen)) {
            const firstIncomplete = this.displaySteps.find(
                (step) => !step.completed,
            );
            if (firstIncomplete) {
                firstIncomplete.isOpen = true;
            } else if (this.displaySteps.length > 0) {
                this.displaySteps[0].isOpen = true;
            }
        }
    }

    toggleSection(stepKey: string) {
        const step = this.displaySteps.find((s) => s.step === stepKey);
        if (!step) return;

        if (!step.isOpen) {
            this.displaySteps.forEach((s) => (s.isOpen = s === step));
        } else {
            step.isOpen = false;
            this.ensureOneSectionOpen();
        }
    }

    toggle(event: Event) {
        if (this.isMobile) {
            this.sidebarVisible = !this.sidebarVisible;
        } else {
            if (this.op) {
                this.op.toggle(event, event.target);
            }
        }
    }

    hide() {
        if (this.isMobile) {
            this.sidebarVisible = false;
        } else {
            if (this.op) this.op.hide();
        }
        this.onboardingGuideService.close();
    }

    getStepConfig(stepKey: string): StepConfig | undefined {
        return this.stepConfigs[stepKey as StepKey];
    }

    executeStepAction(stepKey: string) {
        const config = this.getStepConfig(stepKey);
        if (config && config.enabled(this)) {
            config.action(this);
        }
    }

    isStepEnabled(stepKey: string): boolean {
        const config = this.getStepConfig(stepKey);
        return config ? config.enabled(this) : false;
    }

    navigateToRecipients() {
        if (this.asapFormId) {
            if (this.isMobile) {
                this.hide();
                setTimeout(() => {
                    this.router.navigate([
                        `/assessments/${this.asapFormId}/edit/recipients`,
                    ]);
                }, 100);
                return;
            }
            this.router.navigate([
                `/assessments/${this.asapFormId}/edit/recipients`,
            ]);
        }
    }

    navigateToTeam() {
        if (this.isMobile) {
            this.hide();
        }
        this.router.navigate(["/profile/users"]);
    }

    navigateToAircraft() {
        if (this.isMobile) {
            this.hide();
        }
        this.router.navigate(["/profile/aircraft"]);
    }

    navigateToIntegrations() {
        if (this.isMobile) {
            this.hide();
        }
        this.router.navigate(["/profile/integrations"]);
    }

    navigateToManuals() {
        if (this.isMobile) {
            this.hide();
        }
        this.router.navigateByUrl("/manuals?type=manual");
    }

    printQrCode() {
        if (this.asapFormId) {
            const qrCodeUrl = `/api/assessments/${this.asapFormId}/qr-code?format=pdf`;
            window.open(qrCodeUrl, "_blank");
            this.onboardingGuideService
                .refresh(this.asapFormId || undefined)
                .pipe(untilDestroyed(this))
                .subscribe();
            if (this.isMobile) {
                this.hide();
            }
        }
    }

    markAsNotApplicable(stepKey: string) {
        const step = this.displaySteps.find((s) => s.step === stepKey);
        if (!step || step.completed || !step.canMarkNotApplicable) {
            return;
        }

        const config = this.getStepConfig(stepKey);
        if (!config?.bannerType) {
            return;
        }

        this.userService
            .dismissBanner(config.bannerType, true)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    step.completed = true;
                    const originalStep = this.steps.find(
                        (s) => s.step === stepKey,
                    );
                    if (originalStep) {
                        originalStep.completed = true;
                    }
                    this.onboardingGuideService.setSteps(this.steps);
                    this.onboardingGuideService
                        .refresh(this.asapFormId || undefined)
                        .pipe(untilDestroyed(this))
                        .subscribe();
                    this.stepsChange.emit(this.steps);
                },
                error: () => {
                    this.toastService.error(
                        "Failed to mark step as not applicable",
                    );
                },
            });
    }

    get completedSteps(): number {
        return this.displaySteps.filter((step) => step.completed).length;
    }

    get maxSteps(): number {
        return this.displaySteps.length;
    }
}
